import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowUpRight,
  IconBuilding,
  IconBuildingSkyscraper,
  IconCar,
  IconChartBar,
  IconChevronRight,
  IconDownload,
  IconFileSpreadsheet,
  IconFilter,
  IconHeadset,
  IconHeartHandshake,
  IconReceipt2,
  IconSearch,
  IconTruckDelivery,
  IconX,
} from '@tabler/icons-react';
import api from '../../api/axiosConfig';
import './ManagementReportsPage.css';

/**
 * SMS Grup Yönetim Rapor Merkezi — V2.
 * - Mevcut Excel backend endpointleri değiştirilmez.
 * - axiosConfig baseURL'i /api içeriyor varsayılmıştır.
 * - Her rapor için backend seviyesinde rol/lokasyon yetkisi zorunludur.
 * - POST raporları (abonelik/beton) GET gibi indirmeye zorlanmaz.
 */
const REPORT_MODULES = [
  {
    id: 'fleet',
    title: 'Araç Filo',
    description: 'Araç envanteri, bakım geçmişi, muayene ve poliçe uyarıları',
    shortDescription: 'Araç listeleri, bakım ve kritik tarih raporları',
    category: 'Operasyon',
    icon: IconCar,
    accent: '#4f6bed',
    tint: '#ebefff',
    reports: [
      { id: 'active', title: 'Aktif Araç Listesi', description: 'Aktif araçların kapsamlı envanteri', url: '/vehicles/reports/vehicles/excel', filename: 'Aktif_Arac_Listesi.xlsx' },
      { id: 'archive', title: 'Arşiv Araç Listesi', description: 'Arşivlenmiş araçlar ve bilgileri', url: '/vehicles/reports/archive/excel', filename: 'Arsiv_Arac_Listesi.xlsx' },
      { id: 'maintenance', title: 'Bakım Takvimi ve Geçmişi', description: 'Bakım takvimi ve gerçekleştirilen işlemler', url: '/vehicles/reports/maintenance-calendar/excel', filename: 'Bakim_Takvimi.xlsx' },
      { id: 'inspection', title: 'Muayene Uyarıları', description: 'Muayenesi geçmiş veya yaklaşan araçlar', url: '/vehicles/reports/alerts/inspection/excel', filename: 'Muayene_Uyarilari.xlsx', alertWindow: true },
      { id: 'insurance', title: 'Sigorta Uyarıları', description: 'Trafik sigortası süresi geçmiş veya yaklaşan araçlar', url: '/vehicles/reports/alerts/insurance/excel', filename: 'Sigorta_Uyarilari.xlsx', alertWindow: true },
      { id: 'casco', title: 'Kasko Uyarıları', description: 'Kasko bitişi geçmiş veya yaklaşan araçlar', url: '/vehicles/reports/alerts/casco/excel', filename: 'Kasko_Uyarilari.xlsx', alertWindow: true },
    ],
  },
  {
    id: 'central-office',
    title: 'Merkez Ofis Operasyonları',
    description: 'Merkez ofis araç hareketleri ve misafir ziyaret kayıtları',
    shortDescription: 'Araç hareketleri ve misafir takip raporları',
    category: 'Operasyon',
    icon: IconBuilding,
    accent: '#138b8b',
    tint: '#e6f8f5',
    reports: [
      { id: 'vehicles', title: 'Araç Hareket Raporu', description: 'Araç görev ve dönüş hareketleri', url: '/office-operations/reports/vehicles/export', filename: 'Merkez_Ofis_Arac_Hareketleri.xlsx' },
      { id: 'guests', title: 'Misafir Takip Raporu', description: 'Ziyaretçi giriş, çıkış ve ziyaret süreleri', url: '/office-operations/reports/guests/export', filename: 'Merkez_Ofis_Misafir_Takip.xlsx' },
    ],
  },
  {
    id: 'istanbul-office',
    title: 'İstanbul Ofis Operasyonları',
    description: 'İstanbul ofisinin araç, ziyaretçi ve gider raporları',
    shortDescription: 'Araç, misafir ve gider hareketleri',
    category: 'Operasyon',
    icon: IconBuildingSkyscraper,
    accent: '#8b56ce',
    tint: '#f3eaff',
    reports: [
      { id: 'vehicles', title: 'Araç Hareket Raporu', description: 'Ofis araçlarının görev ve dönüş geçmişi', url: '/istanbul-office/reports/vehicles/export', filename: 'Istanbul_Ofis_Arac_Hareketleri.xlsx' },
      { id: 'guests', title: 'Misafir Ziyaret Raporu', description: 'Ofise gelen ziyaretçiler ve giriş/çıkış kayıtları', url: '/istanbul-office/reports/guests/export', filename: 'Istanbul_Ofis_Misafir_Ziyaret.xlsx' },
      { id: 'expenses', title: 'Ofis Giderleri Raporu', description: 'Kayıtlı ofis harcamaları ve gider dökümü', url: '/istanbul-office/reports/expenses/export', filename: 'Istanbul_Ofis_Giderleri.xlsx' },
    ],
  },
  {
    id: 'concrete',
    title: 'Beton Takip Raporları',
    description: 'Beton teslimatlarının irsaliye, miktar ve lokasyon bazlı kayıtları',
    shortDescription: 'Beton teslimat ve irsaliye kayıtları',
    category: 'Operasyon',
    icon: IconTruckDelivery,
    accent: '#b7782c',
    tint: '#fff3df',
    reports: [
      { id: 'all', title: 'Tüm Beton Kayıtları', description: 'Mevcut tüm kayıtların Excel raporu (filtre uygulanmadan)', mode: 'concrete', filename: 'SMS_Grup_Beton_Raporu.xlsx' },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Abonelik Yönetimi',
    description: 'Elektrik, su, internet ve diğer kurumsal aboneliklerin kaydı',
    shortDescription: 'Abonelik envanteri ve lokasyon özetleri',
    category: 'Finans',
    icon: IconChartBar,
    accent: '#2f8e7b',
    tint: '#e8f8f1',
    reports: [
      { id: 'all', title: 'Tüm Abonelikler', description: 'Tüm abonelikler ile şube ve hizmet türü özetleri', url: '/subscriptions/export/excel', mode: 'subscriptions', filename: 'SMS_Grup_Abonelik_Raporu.xlsx' },
    ],
  },
  {
    id: 'checks',
    title: 'Çek Yönetimi',
    description: 'Çek kayıtları ve firma bilgilerinin dışa aktarımı',
    shortDescription: 'Çek geçmişi ve firma listeleri',
    category: 'Finans',
    icon: IconReceipt2,
    accent: '#d08a32',
    tint: '#fff4e7',
    reports: [
      { id: 'history', title: 'Çek Geçmişi', description: 'Kayıtlı çek hareketleri ve detayları', url: '/check/check-history/export', filename: 'Cek_Gecmisi.xlsx' },
      { id: 'companies', title: 'Firma Listesi', description: 'Çek modülündeki kayıtlı firmalar', url: '/check/companies/export', filename: 'Firma_Listesi.xlsx' },
    ],
  },
  {
    id: 'financial-support',
    title: 'Burs ve Aile Yardımları',
    description: 'Burs alan öğrenciler ve yardım alan ailelerin kayıtları',
    shortDescription: 'Öğrenci bursları ve aile yardım listeleri',
    category: 'Sosyal',
    icon: IconHeartHandshake,
    accent: '#cf5680',
    tint: '#fff0f5',
    sensitive: true,
    reports: [
      { id: 'students', title: 'Burs Alan Öğrenciler', description: 'Öğrenci kayıtları, durum ve burs tutarları', url: '/financial-support/reports/students/excel', filename: 'Ogrenci_Burs_Raporu.xlsx' },
      { id: 'families', title: 'Aile Yardımı Alanlar', description: 'Aile kayıtları, durum ve yardım tutarları', url: '/financial-support/reports/families/excel', filename: 'Aile_Yardim_Raporu.xlsx' },
    ],
  },
  {
    id: 'it',
    title: 'BT Operasyonları',
    description: 'Destek talepleri, çözüm durumu ve operasyonel raporlar',
    shortDescription: 'BT destek yönetimi ve talep analizleri',
    category: 'Destek',
    icon: IconHeadset,
    accent: '#4384c4',
    tint: '#e9f3ff',
    reports: [
      { id: 'tickets', title: 'BT Destek Yönetim Raporu', description: 'Destek talepleri, yönetici özeti ve trend analizleri', url: '/it-support/tickets/export/excel', filename: 'BT_Destek_Yonetim_Raporu.xlsx' },
    ],
  },
];

const FILTERS = ['Tümü', 'Operasyon', 'Finans', 'Destek', 'Sosyal'];
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const clean = (value) => String(value ?? '').toLocaleLowerCase('tr-TR');

function getExcelFilename(disposition, fallback) {
  if (!disposition || typeof disposition !== 'string') return fallback;
  const utf8 = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  const simple = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  let name = fallback;
  try {
    if (utf8) name = decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ''));
    else if (simple) name = simple[1].trim();
  } catch { /* Dosya adı çözümlenemezse sabit ad kullanılır. */ }
  const safe = name.replace(/[\\/\x00-\x1f<>:"|?*]/g, '_').trim();
  return safe.toLowerCase().endsWith('.xlsx') ? safe : fallback;
}

async function downloadError(error) {
  const response = error?.response;
  if (response?.status === 401) return 'Oturum süresi dolmuş. Tekrar giriş yapın.';
  if (response?.status === 403) return 'Bu raporu indirmeye yetkiniz yok.';
  if (response?.status === 404) return 'Rapor servisi bulunamadı veya kayıt yok. Backend endpointini kontrol edin.';
  let detail = response?.data?.detail;
  if (response?.data instanceof Blob) {
    try {
      const body = JSON.parse(await response.data.text());
      detail = body?.detail || body?.message;
    } catch { /* Blob JSON değil */ }
  }
  if (Array.isArray(detail)) detail = detail.map((x) => x.msg || String(x)).join('; ');
  return typeof detail === 'string' ? detail : (error?.message || 'Excel dosyası indirilemedi.');
}

async function fetchReport(report, days) {
  if (report.mode === 'subscriptions') {
    // Mevcut backend body modeli: null => tüm abonelikler.
    return api.post(report.url, { subscription_ids: null }, {
      responseType: 'blob',
      headers: { Accept: XLSX_MIME },
    });
  }
  if (report.mode === 'concrete') {
    // Beton backend'i record_ids istiyor; bu rapor tüm kayıtları kullanır.
    const listing = await api.get('/records/concrete/all');
    if (!Array.isArray(listing.data)) throw new Error('Beton kayıt listesi beklenen formatta değil.');
    const ids = [...new Set(listing.data.map((item) => Number(item.id)))];
    if (ids.some((id) => !Number.isSafeInteger(id) || id < 1)) {
      throw new Error('Beton kayıt numaraları hatalı.');
    }
    if (ids.length === 0) throw new Error('Excel oluşturmak için beton kaydı bulunamadı.');
    if (ids.length > 50000) throw new Error('Tek seferde en fazla 50.000 beton kaydı indirilebilir.');
    return api.post('/records/concrete/export-report', {
      record_ids: ids,
      filter_summary: 'Yönetim Rapor Merkezi — Tüm kayıtlar',
    }, {
      responseType: 'blob',
      headers: { Accept: XLSX_MIME },
    });
  }
  return api.get(report.url, {
    responseType: 'blob',
    params: report.alertWindow ? { days } : undefined,
    headers: { Accept: XLSX_MIME },
  });
}

function saveXlsxResponse(response, fallback) {
  const contentType = String(response.headers?.['content-type'] || response.data?.type || '').toLowerCase();
  if (contentType && !contentType.includes('spreadsheetml.sheet') && !contentType.includes('octet-stream')) {
    throw new Error('Sunucu geçerli bir Excel dosyası döndürmedi.');
  }
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: XLSX_MIME });
  if (!blob.size) throw new Error('Sunucudan boş bir Excel dosyası döndü.');
  const name = getExcelFilename(response.headers?.['content-disposition'], fallback);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  return name;
}

export default function ManagementReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('Tümü');
  const [alertDays, setAlertDays] = useState(30);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');
  const [lastDownload, setLastDownload] = useState('');

  const moduleId = searchParams.get('module');
  const selected = REPORT_MODULES.find((item) => item.id === moduleId) || null;
  const totalReports = useMemo(() => REPORT_MODULES.reduce((sum, item) => sum + item.reports.length, 0), []);
  const visibleModules = useMemo(() => REPORT_MODULES.filter((item) =>
    (activeFilter === 'Tümü' || item.category === activeFilter)
      && clean(`${item.title} ${item.description} ${item.shortDescription} ${item.reports.map((r) => r.title).join(' ')}`).includes(clean(search.trim())),
  ), [activeFilter, search]);
  const visibleReports = useMemo(() => (selected?.reports || []).filter((r) =>
    clean(`${r.title} ${r.description}`).includes(clean(search.trim())),
  ), [selected, search]);

  const switchModule = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('module', id);
    else next.delete('module');
    setSearchParams(next);
    setSearch('');
    setError('');
    setLastDownload('');
  };

  const handleDownload = async (report) => {
    if (downloading) return;
    const key = `${selected?.id}-${report.id}`;
    setDownloading(key);
    setError('');
    setLastDownload('');
    try {
      const result = await fetchReport(report, alertDays);
      const filename = saveXlsxResponse(result, report.filename);
      setLastDownload(`${report.title} — ${filename}`);
    } catch (err) {
      setError(`${report.title}: ${await downloadError(err)}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box className="mrp-page">
      <Container size={1240} className="mrp-container">
        <Box className="mrp-breadcrumb">
          <span>SMS GRUP PORTAL</span>
          <IconChevronRight size={13} />
          {selected ? (
            <>
              <button type="button" className="mrp-breadcrumb-link" onClick={() => switchModule(null)}>Rapor Merkezi</button>
              <IconChevronRight size={13} />
              <strong>{selected.title}</strong>
            </>
          ) : <strong>Rapor Merkezi</strong>}
        </Box>

        {!selected ? (
          <>
            <section className="mrp-hero" aria-label="Yönetim raporları">
              <span className="mrp-hero-orb mrp-hero-orb-a" />
              <span className="mrp-hero-orb mrp-hero-orb-b" />
              <div className="mrp-hero-content">
                <div className="mrp-hero-eyebrow"><IconChartBar size={16} /> MERKEZİ RAPORLAMA</div>
                <Title order={1} className="mrp-hero-title">Tüm raporlar.<br /><span>Tek merkezde.</span></Title>
                <Text className="mrp-hero-description">
                  Departmanların güncel raporlarına tek ekrandan ulaşın, ihtiyacınız olan Excel dosyasını kolayca indirin.
                </Text>
                <div className="mrp-hero-metrics" aria-label="Rapor kataloğu sayıları">
                  <div><strong>{REPORT_MODULES.length}</strong><span>Modül</span></div>
                  <i />
                  <div><strong>{totalReports}</strong><span>Rapor türü</span></div>
                  <i />
                  <div className="mrp-format"><IconFileSpreadsheet size={19} /><span>Excel çıktısı</span></div>
                </div>
              </div>
              <div className="mrp-hero-decoration" aria-hidden="true">
                <div className="mrp-decoration-sheet sheet-back"><IconChartBar size={42} /></div>
                <div className="mrp-decoration-sheet sheet-front"><IconFileSpreadsheet size={65} /></div>
              </div>
            </section>

            <div className="mrp-catalog-heading">
              <div>
                <Text className="mrp-section-eyebrow">RAPOR KATALOĞU</Text>
                <Title order={3} className="mrp-section-title">Modüllere göz atın</Title>
                <Text c="dimmed" size="sm">Raporlarını görüntülemek istediğiniz modülü seçin.</Text>
              </div>
              <div className="mrp-catalog-pill"><IconFileSpreadsheet size={16} /> Excel raporları</div>
            </div>

            <div className="mrp-controls">
              <TextInput
                className="mrp-search"
                leftSection={<IconSearch size={18} />}
                rightSection={search ? <button type="button" className="mrp-clear" aria-label="Aramayı temizle" onClick={() => setSearch('')}><IconX size={16} /></button> : null}
                placeholder="Modül veya rapor adı ara..."
                aria-label="Modül veya rapor ara"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                size="md"
                radius="md"
              />
              <div className="mrp-filters" aria-label="Modül kategorisi">
                <IconFilter className="mrp-filter-icon" size={17} />
                {FILTERS.map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    className={`mrp-filter ${activeFilter === filter ? 'is-active' : ''}`}
                    aria-pressed={activeFilter === filter}
                    onClick={() => setActiveFilter(filter)}
                  >{filter}</button>
                ))}
              </div>
            </div>

            <div className="mrp-grid">
              {visibleModules.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="mrp-module-card"
                    style={{ '--mrp-accent': item.accent, '--mrp-tint': item.tint }}
                    onClick={() => switchModule(item.id)}
                    aria-label={`${item.title}: ${item.reports.length} raporu görüntüle`}
                  >
                    <div className="mrp-card-top">
                      <span className="mrp-card-icon"><Icon size={25} stroke={1.7} /></span>
                      <span className="mrp-card-category">{item.category}</span>
                    </div>
                    <div className="mrp-card-body">
                      <span className="mrp-card-title">{item.title}</span>
                      <span className="mrp-card-description">{item.shortDescription}</span>
                    </div>
                    <div className="mrp-card-bottom">
                      <span><IconFileSpreadsheet size={16} /> {item.reports.length} rapor</span>
                      <span className="mrp-card-arrow"><IconArrowUpRight size={19} stroke={1.7} /></span>
                    </div>
                  </button>
                );
              })}
            </div>

            {visibleModules.length === 0 && (
              <Paper className="mrp-empty" radius="lg" withBorder>
                <IconSearch size={30} stroke={1.4} />
                <Text fw={700}>Eşleşen modül bulunamadı</Text>
                <Text c="dimmed" size="sm">Arama ifadenizi veya kategori filtresini değiştirebilirsiniz.</Text>
                <Button variant="light" color="gray" mt="sm" onClick={() => { setSearch(''); setActiveFilter('Tümü'); }}>Filtreleri temizle</Button>
              </Paper>
            )}
            <Text className="mrp-footer-note">Raporlar mevcut modüllerin veri kaynaklarından hazırlanır. İndirme yetkileri backend tarafında denetlenmelidir.</Text>
          </>
        ) : (
          <Stack gap="lg">
            <div className="mrp-detail-header" style={{ '--mrp-accent': selected.accent, '--mrp-tint': selected.tint }}>
              <button type="button" className="mrp-back" onClick={() => switchModule(null)}><IconArrowLeft size={17} /> Tüm modüller</button>
              <div className="mrp-detail-intro">
                <div className="mrp-detail-icon"><selected.icon size={31} stroke={1.6} /></div>
                <Box className="mrp-detail-heading">
                  <Text className="mrp-section-eyebrow">{selected.category.toLocaleUpperCase('tr-TR')} RAPORLARI</Text>
                  <Title order={2} className="mrp-detail-title">{selected.title}</Title>
                  <Text c="dimmed" size="sm" mt={5}>{selected.description}</Text>
                </Box>
                <div className="mrp-detail-count"><strong>{selected.reports.length}</strong><span>Excel raporu</span></div>
              </div>
            </div>

            {selected.sensitive && (
              <Alert icon={<IconAlertCircle size={19} />} color="orange" radius="md" title="Hassas kişisel veri">
                Bu raporlarda kimlik, iletişim ve finansal bilgiler yer alabilir. Yalnızca yetkili kullanıcılar indirebilmelidir; backend rol kontrolünü doğrulayın.
              </Alert>
            )}
            <div className="mrp-detail-tools">
              <TextInput
                className="mrp-detail-search"
                leftSection={<IconSearch size={17} />}
                placeholder="Bu modülün raporlarında ara..."
                aria-label="Raporlarda ara"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                radius="md"
                size="md"
              />
              {selected.id === 'fleet' && (
                <Paper className="mrp-alert-days" withBorder radius="md">
                  <div><strong>Uyarı aralığı</strong><span>Muayene, sigorta, kasko</span></div>
                  <NumberInput
                    aria-label="Uyarı aralığı gün"
                    min={0}
                    max={365}
                    clampBehavior="strict"
                    value={alertDays}
                    onChange={(value) => setAlertDays(Number.isInteger(value) ? value : 30)}
                    suffix=" gün"
                    w={106}
                    size="sm"
                  />
                </Paper>
              )}
            </div>
            {error && (
              <Alert color="red" radius="md" icon={<IconAlertCircle size={20} />} title="İndirme başarısız" withCloseButton onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {lastDownload && !error && (
              <Alert color="teal" radius="md" withCloseButton onClose={() => setLastDownload('')}>
                Excel dosyası indirilmeye hazırlandı: {lastDownload}
              </Alert>
            )}

            <div className="mrp-report-list">
              {visibleReports.map((report) => {
                const key = `${selected.id}-${report.id}`;
                return (
                  <div className="mrp-report-card" key={key}>
                    <ThemeIcon variant="light" color="teal" size={46} radius="md" className="mrp-report-icon">
                      <IconFileSpreadsheet size={23} stroke={1.6} />
                    </ThemeIcon>
                    <div className="mrp-report-content">
                      <Text fw={700} className="mrp-report-title">{report.title}</Text>
                      <Text c="dimmed" size="sm">{report.description}</Text>
                      <span className="mrp-report-filetag">.XLSX</span>
                    </div>
                    <Button
                      color="teal"
                      variant="filled"
                      radius="md"
                      size="sm"
                      className="mrp-download-button"
                      leftSection={<IconDownload size={17} />}
                      loading={downloading === key}
                      disabled={Boolean(downloading) && downloading !== key}
                      onClick={() => handleDownload(report)}
                    >Excel İndir</Button>
                  </div>
                );
              })}
            </div>
            {visibleReports.length === 0 && (
              <Paper withBorder radius="lg" p="xl" ta="center"><Text c="dimmed">Aradığınız rapor bulunamadı.</Text></Paper>
            )}
            <div className="mrp-detail-footer">
              <IconAlertCircle size={17} />
              Raporlar mevcut backend servislerinden üretilir. Dosya içeriği ve erişim hakları ilgili modüle bağlıdır.
            </div>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
